import React, { useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import SEO from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { ArrowRight, CheckSquare, CurrencyDollar, Package, ShieldCheck } from "@phosphor-icons/react";

const CFG = {
  USD: { sym: "$", rate: 1.0 },
  EUR: { sym: "€", rate: 0.92 },
  GBP: { sym: "£", rate: 0.79 },
};

const TIERS = [
  {
    name: "Starter",
    range: "Up to 500",
    headline: (fmt) => fmt(99),
    sub: "flat",
    note: "One-off list · minimum order",
    features: ["Verified business emails", "Firmographic filter match", "Sample before you pay"],
  },
  {
    name: "Growth",
    range: "500 – 5,000",
    headline: (_, fmtPer) => fmtPer(0.15),
    sub: "/ contact",
    note: "First outbound waves",
    highlight: true,
    features: ["Everything in Starter", "Volume discounting kicks in", "Priority research review"],
  },
  {
    name: "Scale",
    range: "5,000 – 25,000",
    headline: (_, fmtPer, fmtPer3) => `${fmtPer(0.10)} – ${fmtPer3(0.125)}`,
    sub: "/ contact",
    note: "Multi-segment programs",
    features: ["Everything in Growth", "Segment splits at no extra cost", "Dedicated fulfilment analyst"],
  },
  {
    name: "Volume",
    range: "25,000+",
    headline: (_, fmtPer) => `< ${fmtPer(0.10)}`,
    sub: "/ contact · custom",
    note: "Ongoing programs",
    features: ["Everything in Scale", "Rolling refresh schedules", "Contract billing available"],
  },
];

const PRICING_FAQ = [
  { q: "How is the final price calculated?", a: "Each order is quoted after we review your brief. The tier tables are indicative starting points — the final price depends on the depth of human verification required, geography, seniority and any additional data fields." },
  { q: "Is there a minimum order?", a: "Yes, every order has a $99 minimum — roughly one team lunch. That covers up to 500 contacts on the Starter tier." },
  { q: "When do I pay?", a: "Only after you have reviewed the audience estimate and 20-row sample, and approved a quote." },
  { q: "Can I get a custom quote?", a: "Absolutely. For 25,000+ records or ongoing programmes, we quote per project. Describe your audience on the home page to start." },
];

export default function Pricing() {
  const [currency, setCurrency] = useState("USD");
  const { sym, rate } = CFG[currency];
  const fmt = (n) => sym + Math.round(n * rate).toLocaleString();
  const fmtPer = (n) => sym + (n * rate).toFixed(2);
  const fmtPer3 = (n) => {
    // Preserve 3-decimal prices like $0.125 without stripping meaningful precision.
    const v = (n * rate).toFixed(3);
    // Trim trailing zero (0.150 -> 0.15) but keep 0.125 intact.
    return sym + (v.endsWith("0") ? v.slice(0, -1) : v);
  };

  return (
    <div className="App min-h-screen">
      <SEO
        title="Pricing · ExactReach"
        description="Pay per list, never per seat. Verified B2B contacts starting from $99. Simple tiers, no subscription."
      />
      <Navbar />

      {/* Hero */}
      <section className="border-b border-border relative">
        <div className="absolute inset-0 grid-lines opacity-[0.35] pointer-events-none" />
        <div className="relative max-w-6xl mx-auto px-6 lg:px-8 py-20 md:py-28">
          <div className="flex items-center gap-2 mb-4">
            <CurrencyDollar size={22} weight="duotone" className="text-primary" />
            <div className="text-xs tracking-widest-2 uppercase text-muted-foreground font-semibold">Pricing</div>
          </div>
          <h1 className="font-heading text-5xl md:text-6xl lg:text-7xl leading-[0.98] mb-6 max-w-3xl">
            Pay per list. Never per seat.
          </h1>
          <p className="text-[18px] text-muted-foreground max-w-2xl leading-[1.6] mb-8">
            Every ExactReach order starts at $99 — <span className="bg-gradient-to-r from-fuchsia-600 via-rose-500 to-orange-500 bg-clip-text text-transparent font-bold">roughly your team's Thursday lunch</span>. Bigger lists get cheaper per contact. Every price is for verified data, <span className="bg-gradient-to-r from-fuchsia-600 via-rose-500 to-orange-500 bg-clip-text text-transparent font-bold">and no, there's no "premium deliverability" upsell hiding at checkout</span>.
          </p>

          <div className="inline-flex border border-border p-1 bg-white rounded-sm" data-testid="pricing-currency-toggle">
            {["USD", "EUR", "GBP"].map((c) => (
              <button
                key={c}
                data-testid={`pricing-currency-${c.toLowerCase()}`}
                onClick={() => setCurrency(c)}
                className={`px-4 py-2 text-[13px] font-semibold rounded-sm transition-colors ${currency === c ? "bg-foreground text-background" : "text-muted-foreground hover:text-foreground"}`}
              >
                {c}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Tiers */}
      <section className="py-24 md:py-28 border-b border-border">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-border" data-testid="pricing-tiers">
            {TIERS.map((t) => (
              <div
                key={t.name}
                data-testid={`pricing-tier-${t.name.toLowerCase()}`}
                className={`relative bg-white p-8 flex flex-col ${t.highlight ? "ring-2 ring-primary z-10" : ""}`}
              >
                {t.highlight && (
                  <div className="absolute -top-3 left-6 bg-primary text-primary-foreground text-[10px] font-mono uppercase tracking-widest-2 font-bold px-2 py-1">Most popular</div>
                )}
                <div className="text-xs font-mono uppercase tracking-widest-2 text-muted-foreground mb-2">{t.name}</div>
                <div className="font-heading text-lg leading-tight mb-6 font-semibold">
                  {t.range}<span className="text-muted-foreground text-[13px] font-medium"> contacts</span>
                </div>
                <div className="mb-6">
                  <div className={`font-heading tracking-tighter leading-none font-bold text-primary ${t.custom ? "text-3xl" : "text-4xl"}`}>
                    {t.headline(fmt, fmtPer, fmtPer3)}
                  </div>
                  <div className="text-xs font-mono text-muted-foreground mt-2">{t.sub}</div>
                </div>
                <ul className="space-y-2.5 text-sm mb-6">
                  {t.features.map((f) => (
                    <li key={f} className="flex gap-2">
                      <CheckSquare weight="fill" size={16} className="mt-0.5 text-primary shrink-0" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <div className="text-xs text-muted-foreground mt-auto pt-4 border-t border-border">{t.note}</div>
              </div>
            ))}
          </div>

          <div className="mt-6 text-xs text-muted-foreground font-mono">
            Every order has a {fmt(99)} minimum. Prices shown are indicative — final quote depends on the depth of human verification required.
          </div>

          <div className="mt-10 border border-border bg-white p-6 md:p-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div className="flex items-start gap-3">
              <ShieldCheck size={26} weight="duotone" className="text-primary mt-0.5 shrink-0" />
              <div className="text-[15px] text-muted-foreground leading-[1.6] max-w-2xl">
                <b className="text-foreground">Every quote is bespoke.</b> Describe your audience in plain English on the home page and we will confirm the exact price, estimated volume and a 20-record sample before you pay.
              </div>
            </div>
            <Link to="/#query">
              <Button className="rounded-sm" data-testid="pricing-cta-quote">
                Get my exact quote <ArrowRight size={14} className="ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* What's included */}
      <section className="py-24 md:py-28 border-b border-border bg-muted/30">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <div className="flex items-center gap-2 mb-4">
            <Package size={22} weight="duotone" className="text-primary" />
            <div className="text-xs tracking-widest-2 uppercase text-muted-foreground font-semibold">What's included</div>
          </div>
          <h2 className="font-heading text-3xl md:text-5xl tracking-tighter mb-10 max-w-3xl">
            Every price already includes verification.
          </h2>
          <div className="grid md:grid-cols-2 gap-4">
            {[
              { t: "Business email re-verification", d: "Every email is re-checked close to fulfilment. Invalid entries are removed or replaced within tolerance." },
              { t: "Firmographic validation", d: "Company name, industry, size and other filters are validated against multiple sources before delivery." },
              { t: "20-record sample", d: "Reviewed before you pay so you can confirm the interpretation of your brief and the field quality." },
              { t: "Secure delivery", d: "Encrypted, time-limited download links. No email attachments and no permanent public URLs." },
            ].map((x) => (
              <div key={x.t} className="border border-border bg-white p-6">
                <div className="flex items-center gap-2 mb-2">
                  <CheckSquare weight="fill" size={18} className="text-primary" />
                  <h3 className="font-heading text-lg font-semibold">{x.t}</h3>
                </div>
                <p className="text-[15px] text-muted-foreground leading-[1.6]">{x.d}</p>
              </div>
            ))}
          </div>
          <p className="text-xs text-muted-foreground mt-6 font-mono">
            Phone verification, direct dial validation and deep manual research may be quoted separately.
          </p>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 md:py-28 border-b border-border">
        <div className="max-w-6xl mx-auto px-6 lg:px-8 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <div className="text-xs tracking-widest-2 uppercase text-muted-foreground font-semibold mb-3">Pricing FAQ</div>
            <h2 className="font-heading font-bold text-3xl md:text-5xl tracking-tighter">Common questions.</h2>
          </div>
          <div className="lg:col-span-8">
            {PRICING_FAQ.map((f) => (
              <details key={f.q} className="group border-b border-border py-5">
                <summary className="flex items-center justify-between cursor-pointer list-none">
                  <span className="font-semibold text-base pr-4">{f.q}</span>
                  <span className="text-2xl leading-none text-muted-foreground group-open:rotate-45 transition-transform">+</span>
                </summary>
                <p className="text-sm text-muted-foreground mt-3 leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
